import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { history } from '../../store';
import { resetParams } from './securityReducer';
import callApi, { saveTokens } from '../../service/callApi';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import sendRequest from '../../service/sendRequest';

const CHECK_AUTH = 'securityActions/CHECK_AUTH';
const LOGIN = 'securityActions/LOGIN';
const CONFIRM_LOGIN = 'securityActions/CONFIRM_LOGIN';
const FORGOT_PASSWORD = 'securityActions/FORGOT_PASSWORD';
const SEND_LOGIN_AGAIN = 'securityActions/SEND_LOGIN_AGAIN';
const SEND_FORGOT_PASSWORD_AGAIN = 'securityActions/SEND_FORGOT_PASSWORD_AGAIN';
const CHANGE_PASSWORD = 'securityActions/CHANGE_PASSWORD';
const CONFIRM_CHANGE_PASSWORD = 'securityActions/CONFIRM_CHANGE_PASSWORD';
const GET_USER_ROLES = 'securityActions/GET_USER_ROLES';
const GET_CURRENT_USER_INFO = 'securityActions/GET_CURRENT_USER_INFO';
const GET_SERVER_URL = 'securityActions/GET_SERVER_URL';
const GET_CRAWL_SERVER_URL = 'securityActions/GET_CRAWL_SERVER_URL';

export const checkAuth = createAsyncThunk(CHECK_AUTH, async (_, { dispatch }) => {
  await sendRequest(() => callApi.request({ url: `/api/check-auth` }), {
    onError: () => null,
  });

  dispatch(getCurrentUserRoles());
  dispatch(getCurrenUserInfo());
  dispatch(getServerUrl());
  dispatch(getCrawlServerUrl());
});

export const login = createAsyncThunk(
  LOGIN,
  async ({ username, password, rememberMe, isRepeat = false }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(() => axios.post(`/api/auth/login`, { username, password, rememberMe }), {
      onError: (e) => {
        if (e?.response?.status === 403) {
          dispatch(
            enqueueSnackbar({
              message: 'wrongCredentials',
              options: {
                key: new Date().getTime() + Math.random(),
                variant: 'error',
                subMessage: 'contactAdministrator',
              },
            }),
          );

          return rejectWithValue(e);
        }

        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    });

    const { uuid } = response.data;

    if (!isRepeat) {
      history.push('/otp');
    }

    return { uuid, username, password, rememberMe };
  },
);

export const confirmLogin = createAsyncThunk(CONFIRM_LOGIN, async ({ otp }, { getState, dispatch }) => {
  const { username, uuid } = getState().securityNew;

  if (!username || !uuid) {
    dispatch(resetParams());
    dispatch(somethingWrongNotification());
    history.push('/login');

    throw new Error();
  }

  const response = await sendRequest(() => axios.post('/api/auth/confirm', { username, uuid, otp }), {
    onError: (e) => {
      if (!e.response) {
        dispatch(resetParams());
        dispatch(somethingWrongNotification());
        history.push('/login');
      }

      if (e.response.status === 400) {
        const { attemptsLeft = 0 } = e.response.data;
        let attempts = attemptsLeft + 1;

        dispatch(
          enqueueSnackbar({
            message: 'incorrect2FACode',
            options: {
              variant: 'error',
              messageParams: {
                attempts,
              },
            },
          }),
        );
      }

      if (e.response.status === 404) {
        dispatch(
          enqueueSnackbar({
            message: '2FAAttemptsLimitReached',
            options: {
              variant: 'error',
            },
          }),
        );
        dispatch(resetParams());
        history.push('/login');
      }

      if (e.response.status === 422) {
        dispatch(
          enqueueSnackbar({
            message: '2FACodeHasBeenDestroyed',
            options: {
              variant: 'error',
            },
          }),
        );
        dispatch(resetParams());
        history.push('/login');
      }
    },
  });

  await saveTokens(response.data);
  history.push('/projects');
});

export const sendForgotPasswordOtp = createAsyncThunk(
  FORGOT_PASSWORD,
  async ({ username, isRepeat = false }, { dispatch }) => {
    const response = await sendRequest(() => axios.post('/api/forgot-password/forgot-password-otp', { username }), {
      onError: (e) => {
        if (e?.response?.status === 403) {
          dispatch(
            enqueueSnackbar({
              message: 'incorrectLogin',
              options: {
                variant: 'error',
              },
            }),
          );
        } else {
          dispatch(somethingWrongNotification());
        }
      },
    });

    const { uuid } = response.data;

    if (!isRepeat) {
      history.push('/email-sent');
    }

    return { uuid, username };
  },
);

export const sendLoginOtpAgain = createAsyncThunk(SEND_LOGIN_AGAIN, async (_, { getState, dispatch }) => {
  const { username, password, rememberMe } = getState().securityNew;
  dispatch(login({ username, password, rememberMe, isRepeat: true }));
});

export const sendForgotPasswordOtpAgain = createAsyncThunk(
  SEND_FORGOT_PASSWORD_AGAIN,
  async (_, { getState, dispatch }) => {
    const { username } = getState().securityNew;
    dispatch(sendForgotPasswordOtp({ username, isRepeat: true }));
  },
);

export const confirmChangePassword = createAsyncThunk(
  CONFIRM_CHANGE_PASSWORD,
  async ({ otp }, { getState, dispatch }) => {
    const { username, uuid } = getState().securityNew;

    const response = await sendRequest(() => axios.post('/api/forgot-password/confirm', { username, uuid, otp }), {
      onError: (e) => {
        if (!e.response) {
          dispatch(resetParams());
          dispatch(somethingWrongNotification());
          history.push('/forgot-password');
        }

        if (e.response.status === 400) {
          const { attemptsLeft = 0 } = e.response.data;
          let attempts = attemptsLeft + 1;

          dispatch(
            enqueueSnackbar({
              message: 'incorrectForgotPasswordCode',
              options: {
                variant: 'error',
                messageParams: {
                  attempts,
                },
              },
            }),
          );
        }

        if (e.response.status === 404) {
          dispatch(
            enqueueSnackbar({
              message: 'forgotPasswordAttemptsLimitReached',
              options: {
                variant: 'error',
              },
            }),
          );

          dispatch(resetParams());
          history.push('/forgot-password');
        }
      },
    });

    const { changePasswordToken } = response.data;
    history.push('/change-password');
    return { changePasswordToken };
  },
);

export const changeUserPassword = createAsyncThunk(CHANGE_PASSWORD, async ({ newPassword }, { getState, dispatch }) => {
  const { username, changePasswordToken } = getState().securityNew;

  await sendRequest(
    () =>
      axios.post(`/api/forgot-password/change-password `, {
        username,
        changePasswordToken,
        newPassword,
      }),
    {
      onError: () => {
        dispatch(
          enqueueSnackbar({
            message: 'passwordWasNotChanged',
            options: {
              variant: 'error',
              messageParams: {
                username,
              },
            },
          }),
        );
        dispatch(resetParams());
        history.push('/login');
      },
    },
  );

  dispatch(
    enqueueSnackbar({
      message: 'passwordHasBeenChanged',
      options: {
        variant: 'success',
        messageParams: {
          username,
        },
      },
    }),
  );
  dispatch(resetParams());

  history.push('/login');
});

export const getCurrentUserRoles = createAsyncThunk(GET_USER_ROLES, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/user-data/current-user-roles`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const getCurrenUserInfo = createAsyncThunk(GET_CURRENT_USER_INFO, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/user-data`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const getServerUrl = createAsyncThunk(GET_SERVER_URL, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => axios.get(`/api/consumer/server`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const getCrawlServerUrl = createAsyncThunk(GET_CRAWL_SERVER_URL, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/sonar-data/scrapper/address`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});
